<script>
  import gql from 'graphql-tag';
  import { client } from 'utils/apollo.js';
  import { onMount, getContext } from 'svelte';
  import debounce from 'lodash/debounce';

  import { productDataStore } from '../../stores/ProductData.js';
  import { webPSupport } from '../../stores/WebP.js';
  import { isMobile, designTabActive } from '../../stores/Designer.js';
  import { artPanelState, artCategoryId, artSelectionId } from '../../stores/ArtPanelState.js';

  // import Search from '../../svg/Search.svelte';
  import DesignCard from './DesignCard.svelte';
  import ArtCollectionCard from './ArtCollectionCard.svelte';
  import FontOptions from './FontOptions.svelte';
  import FontColour from './FontColour.svelte';
  import ColoredButton from './ColoredButton.svelte';
  import PrintColourSelect from './PrintColourSelect.svelte';
  import ColourPicker from './ColourPicker.svelte';
  import Switch from '../Switch.svelte';
  import PrintTypeSelection from './PrintTypeSelection.svelte';

  export let addToKonva;
  export let segment;
  export let updateInKonva;
  export let editingElement;
  export let icons;

  let printOptions = $productDataStore[$productDataStore.showing].printOptions;

  let printOption = printOptions[0]?.PrintOption?.name || 'digital transfer';

  //https://svelte.dev/repl/0ace7a508bd843b798ae599940a91783?version=3.16.7
  //Function to detect click outside of colour picker selectors - so we close them
  function clickOutside(node) {
    const handleClick = (event) => {
      if (node && !node.contains(event.target) && !event.defaultPrevented) {
        node.dispatchEvent(new CustomEvent('click_outside', node));
      }
    };
    document.addEventListener('click', handleClick, true);
    return {
      destroy() {
        document.removeEventListener('click', handleClick, true);
      },
    };
  }

  // We have multiple colour pickers so we will store their state in array
  let colourPickerShow = [false];

  // This function is called by each colour picker when a click is outside
  // Sends the index of the colourPicker in the colourPickerShow array
  function handleClickOutside(index) {
    colourPickerShow[index] = false;
  }

  let ext;
  if ($webPSupport) {
    ext = 'webp';
  } else {
    ext = 'png';
  }

  let collections = []; //To hold art collections data

  let textArray = [];
  let artColourArray = [];
  let showBackground = true;

  // Need a default here to prevent errors?
  let art = {
    name: 'shield and banner',
    id: 1,
    image: 'https://images.uniformhero.com.au/art/soccer-decal-1.webp',
    backgroundAvailable: true,
    defaultArtColourArray: [
      {
        fill: 'black',
        rgb: '(0,0,0)',
      },
      {
        fill: 'light-blue',
        rgb: '(66, 162, 220)',
      },
    ],
    defaultFont: {
      name: 'apache',
      svg: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 30"><path d="M10.57 17.52V25H5.79V5.84l3.67-3.6h9.94V25h-4.77v-7.48h-4.06zm0-4.33h4.06V6.57h-4.06v6.62zm15.733 6.03V25h-4.77V5.45l3.44-3.21h9.77v16.98h-8.44zm0-4.36h3.67V6.57h-3.67v8.29zm15.248 2.66V25h-4.78V5.84l3.67-3.6h9.94V25h-4.77v-7.48h-4.06zm0-4.33h4.06V6.57h-4.06v6.62zm15.412 7.5h3.84v-2.77h4.79v3.93L62.443 25h-10.26V5.37l3.4-3.13h10.01v8.59h-4.79V6.57h-3.84v14.12zM72.271 2.24v9.35h4.09V2.24h4.77V25h-4.77v-9.1h-4.09V25H67.5V2.24h4.77zM83.164 25V2.24h11.05v4.33h-6.33v4.39h6.01v4.25h-5.95v5.48h6.43V25h-11.21z" transform="translate(-.082 1.38)"/></svg>',
    },
    numberOfColours: 2,
    numberOfTextLines: 2,
    type: 'multicolour',
    defaultTextColourRGB: '(0,0,0)',
    defaultTextColourFill: 'black',
  };

  let font = {
    fontFamily: {
      name: 'aerovias',
      svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 30"><path d="M18.77 20.04h-8.15q-.06.45-.1.91-.03.45-.03.91 0 2.41.94 4.38.94 1.97 2.65 3.65h-3.77q-1.33-1.75-1.96-3.82-.62-2.08-.62-4.25 0-.87.1-1.78H4.37l.73-2.8h3.29q.52-1.85 1.5-3.77t2.34-3.7q1.36-1.78 3.02-3.34 1.66-1.55 3.53-2.72 1.87-1.18 3.86-1.84 2-.66 4.06-.66L20.3 25h-2.86l1.33-4.96zm.76-2.8 3.32-12.43q-1.95.87-3.75 2.14-1.8 1.28-3.32 2.89-1.52 1.6-2.69 3.47t-1.84 3.93h8.28zM30.081 25h-8.03l4.82-18.06h8.04l-.74 2.76h-5.17l-1.29 4.89h3.84l-.73 2.76h-3.88l-1.29 4.85h5.17l-.74 2.8zm2.308 0 4.82-18.06h3.99q1.08 0 2.02.36.95.37 1.65 1.05.69.68 1.1 1.61.4.93.4 2.01 0 1.15-.4 2.23-.41 1.09-1.1 2-.7.9-1.63 1.62-.93.72-1.97 1.14L42.56 25h-2.83l-1.15-5.49h-1.86L35.26 25h-2.87zm5.07-8.25h1.12q.87 0 1.78-.4t1.61-1.08q.69-.68 1.15-1.54.45-.85.45-1.76 0-1.08-.66-1.68-.67-.59-1.71-.59h-1.86l-1.88 7.05zm17.987-9.81q1.26 0 2.17.56.91.56 1.48 1.43.58.87.86 1.97t.28 2.19q0 1.71-.65 3.79-.64 2.08-1.81 3.88-1.17 1.79-2.82 3.02-1.64 1.22-3.59 1.22-1.26 0-2.17-.56-.91-.56-1.48-1.45-.58-.89-.86-1.99t-.28-2.18q0-1.12.3-2.45.29-1.33.84-2.65.54-1.33 1.32-2.56.79-1.22 1.78-2.16 1-.94 2.15-1.5 1.16-.56 2.48-.56zm0 2.76q-1.32 0-2.44 1.01-1.12 1.01-1.94 2.45-.82 1.43-1.28 2.98-.45 1.56-.45 2.68 0 .49.09 1.06.08.58.31 1.1.23.53.61.88.39.34 1.02.34 1.32 0 2.44-.99 1.12-1 1.92-2.43.81-1.43 1.26-3 .46-1.58.46-2.69 0-.46-.09-1.05-.09-.6-.3-1.12-.21-.52-.59-.87-.39-.35-1.02-.35zm18.91-2.76q.1.59.17 1.17.07.57.07 1.17 0 3.07-1.38 5.87-1.38 2.79-3.51 4.92-.94.95-2.1 1.82-1.15.87-2.44 1.57-1.29.7-2.66 1.12-1.36.42-2.69.42l4.82-18.06h2.87l-3.77 14.08q1.6-.81 3.07-2.05 1.47-1.24 2.57-2.77 1.1-1.54 1.76-3.31.67-1.76.67-3.61 0-1.22-.32-2.34h2.87zm6.041 0L75.577 25h-2.87l4.82-18.06h2.87zm5.831 14.99h-5.13q0 .24-.02.47-.02.22-.02.47 0 1.01.28 2.13h-2.83q-.21-1.05-.21-2.13 0-3.08 1.36-5.87 1.37-2.8 3.53-4.93.84-.84 2.05-1.73 1.2-.89 2.56-1.66 1.37-.76 2.75-1.25t2.57-.49L88.288 25h-2.86l.8-3.07zm-4.47-2.76h5.24l2.13-8.04q-2.48 1.22-4.43 3.32-1.96 2.1-2.94 4.72zm8.978 3.03h3.29q.56 0 1.15-.48.59-.49.59-1.12l-1.57-8.63q-.03-.18-.05-.35-.02-.18-.02-.35 0-.87.39-1.66.38-.79 1.01-1.38.63-.59 1.45-.94t1.66-.35h3.81l-.74 2.76q-.28 0-.75-.04-.47-.03-1.03-.03-.52 0-1.06.05-.55.05-.98.23-.44.17-.72.47-.28.3-.28.78v.14l1.57 8.63q.04.18.06.35.01.18.01.35 0 .88-.38 1.66-.39.79-1.01 1.4-.63.61-1.45.96-.83.35-1.66.35h-4.02l.73-2.8z" transform="translate(-3.408 -.55)"/></svg>`,
    },
    fill: 'White',
    rgb: '(255, 255, 255)',
  };

  // Have a flag for loading of $editingElement
  // Turn off editing if transformer turns off?
  let editingElementLoaded = false;
  $: if ($editingElement && !editingElementLoaded) {
    // set all variables to values from editing element
    artSelectionId.set($editingElement.id);
    artPanelState.set('art-item');

    art = $editingElement.art;
    // Need to run function to get svg that matches name
    let fontElement = fonts.family.find((x) => x.name === $editingElement.font);
    if (fontElement) {
      font.fontFamily.name = fontElement.name;
      font.fontFamily.svg = fontElement.svg;
    } else {
      // Could not find matching font?
      // Use default of apache
      font.fontFamily.name = 'apache';
      font.fontFamily.svg =
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 30"><path d="M10.57 17.52V25H5.79V5.84l3.67-3.6h9.94V25h-4.77v-7.48h-4.06zm0-4.33h4.06V6.57h-4.06v6.62zm15.733 6.03V25h-4.77V5.45l3.44-3.21h9.77v16.98h-8.44zm0-4.36h3.67V6.57h-3.67v8.29zm15.248 2.66V25h-4.78V5.84l3.67-3.6h9.94V25h-4.77v-7.48h-4.06zm0-4.33h4.06V6.57h-4.06v6.62zm15.412 7.5h3.84v-2.77h4.79v3.93L62.443 25h-10.26V5.37l3.4-3.13h10.01v8.59h-4.79V6.57h-3.84v14.12zM72.271 2.24v9.35h4.09V2.24h4.77V25h-4.77v-9.1h-4.09V25H67.5V2.24h4.77zM83.164 25V2.24h11.05v4.33h-6.33v4.39h6.01v4.25h-5.95v5.48h6.43V25h-11.21z" transform="translate(-.082 1.38)"/></svg>';
    }
    font.fill = $editingElement.textColour.name;
    font.rgb = $editingElement.textColour.rgb;

    // set art colour to decal defaults
    artColourArray = JSON.parse($editingElement.artColourArray);

    // set text array to decal defaults
    textArray = JSON.parse($editingElement.textArray);

    // add data to colourPicker show array to hide colour pickers
    colourPickerShow = [false]; // One for font colour, remainder for colours
    for (let i; i < art.numberOfColours; i++) {
      colourPickerShow.push(false);
    }

    printOption = $editingElement.printOption;
    editingElementLoaded = true;
  }

  // When transformer is detached editingElement becomes null
  // Need to reset variables to defaults in this scenario
  $: if (!$editingElement && editingElementLoaded) {
    (async () => {
      // set variables back to defaults
      collections = await queryArtCollections();

      artSelectionId.set(null);
      artPanelState.set('categories');

      art = {
        name: 'shield and banner',
        id: 1,
        image: 'https://images.uniformhero.com.au/art/soccer-decal-1.webp',
        backgroundAvailable: true,
        defaultArtColourArray: [
          {
            fill: 'black',
            rgb: '(0,0,0)',
          },
          {
            fill: 'light-blue',
            rgb: '(66, 162, 220)',
          },
        ],
        defaultFont: {
          name: 'apache',
          svg: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 30"><path d="M10.57 17.52V25H5.79V5.84l3.67-3.6h9.94V25h-4.77v-7.48h-4.06zm0-4.33h4.06V6.57h-4.06v6.62zm15.733 6.03V25h-4.77V5.45l3.44-3.21h9.77v16.98h-8.44zm0-4.36h3.67V6.57h-3.67v8.29zm15.248 2.66V25h-4.78V5.84l3.67-3.6h9.94V25h-4.77v-7.48h-4.06zm0-4.33h4.06V6.57h-4.06v6.62zm15.412 7.5h3.84v-2.77h4.79v3.93L62.443 25h-10.26V5.37l3.4-3.13h10.01v8.59h-4.79V6.57h-3.84v14.12zM72.271 2.24v9.35h4.09V2.24h4.77V25h-4.77v-9.1h-4.09V25H67.5V2.24h4.77zM83.164 25V2.24h11.05v4.33h-6.33v4.39h6.01v4.25h-5.95v5.48h6.43V25h-11.21z" transform="translate(-.082 1.38)"/></svg>',
        },
        numberOfColours: 2,
        numberOfTextLines: 2,
        type: 'multicolour',
        defaultTextColourRGB: '(0,0,0)',
        defaultTextColourFill: 'black',
      };

      font = {
        fontFamily: {
          name: 'Aerovias',
          svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 30"><path d="M18.77 20.04h-8.15q-.06.45-.1.91-.03.45-.03.91 0 2.41.94 4.38.94 1.97 2.65 3.65h-3.77q-1.33-1.75-1.96-3.82-.62-2.08-.62-4.25 0-.87.1-1.78H4.37l.73-2.8h3.29q.52-1.85 1.5-3.77t2.34-3.7q1.36-1.78 3.02-3.34 1.66-1.55 3.53-2.72 1.87-1.18 3.86-1.84 2-.66 4.06-.66L20.3 25h-2.86l1.33-4.96zm.76-2.8 3.32-12.43q-1.95.87-3.75 2.14-1.8 1.28-3.32 2.89-1.52 1.6-2.69 3.47t-1.84 3.93h8.28zM30.081 25h-8.03l4.82-18.06h8.04l-.74 2.76h-5.17l-1.29 4.89h3.84l-.73 2.76h-3.88l-1.29 4.85h5.17l-.74 2.8zm2.308 0 4.82-18.06h3.99q1.08 0 2.02.36.95.37 1.65 1.05.69.68 1.1 1.61.4.93.4 2.01 0 1.15-.4 2.23-.41 1.09-1.1 2-.7.9-1.63 1.62-.93.72-1.97 1.14L42.56 25h-2.83l-1.15-5.49h-1.86L35.26 25h-2.87zm5.07-8.25h1.12q.87 0 1.78-.4t1.61-1.08q.69-.68 1.15-1.54.45-.85.45-1.76 0-1.08-.66-1.68-.67-.59-1.71-.59h-1.86l-1.88 7.05zm17.987-9.81q1.26 0 2.17.56.91.56 1.48 1.43.58.87.86 1.97t.28 2.19q0 1.71-.65 3.79-.64 2.08-1.81 3.88-1.17 1.79-2.82 3.02-1.64 1.22-3.59 1.22-1.26 0-2.17-.56-.91-.56-1.48-1.45-.58-.89-.86-1.99t-.28-2.18q0-1.12.3-2.45.29-1.33.84-2.65.54-1.33 1.32-2.56.79-1.22 1.78-2.16 1-.94 2.15-1.5 1.16-.56 2.48-.56zm0 2.76q-1.32 0-2.44 1.01-1.12 1.01-1.94 2.45-.82 1.43-1.28 2.98-.45 1.56-.45 2.68 0 .49.09 1.06.08.58.31 1.1.23.53.61.88.39.34 1.02.34 1.32 0 2.44-.99 1.12-1 1.92-2.43.81-1.43 1.26-3 .46-1.58.46-2.69 0-.46-.09-1.05-.09-.6-.3-1.12-.21-.52-.59-.87-.39-.35-1.02-.35zm18.91-2.76q.1.59.17 1.17.07.57.07 1.17 0 3.07-1.38 5.87-1.38 2.79-3.51 4.92-.94.95-2.1 1.82-1.15.87-2.44 1.57-1.29.7-2.66 1.12-1.36.42-2.69.42l4.82-18.06h2.87l-3.77 14.08q1.6-.81 3.07-2.05 1.47-1.24 2.57-2.77 1.1-1.54 1.76-3.31.67-1.76.67-3.61 0-1.22-.32-2.34h2.87zm6.041 0L75.577 25h-2.87l4.82-18.06h2.87zm5.831 14.99h-5.13q0 .24-.02.47-.02.22-.02.47 0 1.01.28 2.13h-2.83q-.21-1.05-.21-2.13 0-3.08 1.36-5.87 1.37-2.8 3.53-4.93.84-.84 2.05-1.73 1.2-.89 2.56-1.66 1.37-.76 2.75-1.25t2.57-.49L88.288 25h-2.86l.8-3.07zm-4.47-2.76h5.24l2.13-8.04q-2.48 1.22-4.43 3.32-1.96 2.1-2.94 4.72zm8.978 3.03h3.29q.56 0 1.15-.48.59-.49.59-1.12l-1.57-8.63q-.03-.18-.05-.35-.02-.18-.02-.35 0-.87.39-1.66.38-.79 1.01-1.38.63-.59 1.45-.94t1.66-.35h3.81l-.74 2.76q-.28 0-.75-.04-.47-.03-1.03-.03-.52 0-1.06.05-.55.05-.98.23-.44.17-.72.47-.28.3-.28.78v.14l1.57 8.63q.04.18.06.35.01.18.01.35 0 .88-.38 1.66-.39.79-1.01 1.4-.63.61-1.45.96-.83.35-1.66.35h-4.02l.73-2.8z" transform="translate(-3.408 -.55)"/></svg>`,
        },
        fill: 'White',
        rgb: '(255, 255, 255)',
      };

      textArray = [];
      artColourArray = [];
      showBackground = true;
      printOption = printOptions[0]?.PrintOption?.name || 'digital transfer';

      // We've reset to default so mark editing loaded back to false so this block doesn't run again
      editingElementLoaded = false;
    })();
  }

  const designerFonts = getContext('designerFonts');
  const heatTransferPrintColours = getContext('heatTransferPrintColours');

  // Background colour of selected product item - to show preview against
  let selectedProductColourMain = $productDataStore[$productDataStore.showing].selectedColour.ProductColoursMasterMain;

  let selectedProductColourTrim1 =
    $productDataStore[$productDataStore.showing].selectedColour.ProductColoursMasterTrim1;

  let selectedProductColourTrim2 =
    $productDataStore[$productDataStore.showing].selectedColour.ProductColoursMasterTrim2;

  let fonts = {
    family: [],
    color: [],
  };

  fonts.family = designerFonts.map((item) => {
    return { name: item.name, svg: item.svg };
  });

  fonts.colour = heatTransferPrintColours.map((item) => {
    return { name: item.colour, rgb: item.colourRgb };
  });

  async function queryArtCollections() {
    const ARTCOLLECTIONS = gql`
      query MyQuery {
        DesignerArtCollections(order_by: { name: asc }, where: { visible: { _eq: true } }) {
          id
          name
          image
        }
      }
    `;

    const res = await client.query({ query: ARTCOLLECTIONS });
    const data = res.data.DesignerArtCollections;

    return data;
  }

  let items = [];

  const subCategoryQueryString = () => {
    return `
    query QuerySubCategory($_eq: Int!) @cached {
        DesignerArts(where: {DesignerArtCollection: {id: {_eq: $_eq}}, visible: {_eq: true}}, order_by: {id: asc}) {
            id
            name
            backgroundAvailable
            backgroundCanBeRemoved
            defaultTextArray
            defaultArtColourArray
            defaultFont
            numberOfColours
            numberOfTextLines
            previewImages
            type
            defaultTextColourRGB
            defaultTextColourFill
        }
    }
    `;
  };

  async function querySubCategory(id) {
    items = [];
    const DECALS = gql`
      ${subCategoryQueryString()}
    `;

    const res = await client.query({
      query: DECALS,
      variables: {
        _eq: id,
      },
    });
    const data = res.data.DesignerArts;

    // Filter out any decals that don't have preview images
    items = data.filter(function (item) {
      if (item.previewImages) {
        return true;
      } else {
        return false;
      }
    });

    // Map data to item
    items = data.map((item) => {
      let images = JSON.parse(item.previewImages);

      return {
        name: item.name,
        id: item.id,
        image: images.background.regular[ext],
        backgroundAvailable: item.backgroundAvailable,
        backgroundCanBeRemoved: item.backgroundCanBeRemoved,
        defaultTextArray: JSON.parse(item.defaultTextArray),
        defaultArtColourArray: JSON.parse(item.defaultArtColourArray),
        defaultFont: JSON.parse(item.defaultFont),
        numberOfColours: item.numberOfColours,
        numberOfTextLines: item.numberOfTextLines,
        type: item.type,
        defaultTextColourRGB: item.defaultTextColourRGB,
        defaultTextColourFill: item.defaultTextColourFill,
      };
    });
  }

  async function selectCategory(collectionId) {
    artPanelState.set('subcategories');

    // const id = this.dataset.idx;
    artCategoryId.set(collectionId);
    await querySubCategory(collectionId);
  }

  // Debounce updates on text input
  const updateTextArray = debounce((event) => {
    textArray = []; //reset array
    let inputs = document.querySelectorAll('input.art-input');
    for (let i = 0; i < inputs.length; i++) {
      let value = inputs[i].value;
      if (value) {
        textArray.push(value);
      }
    }
  }, 300);

  async function selectArt(item) {
    artSelectionId.set(item.id);
    artPanelState.set('art-item');

    art = item;
    font.fontFamily = item.defaultFont;
    font.fill = item.defaultTextColourFill;
    font.rgb = item.defaultTextColourRGB;

    // set art colour to decal defaults
    artColourArray = art.defaultArtColourArray;

    // set text array to decal defaults
    textArray = art.defaultTextArray;

    // add data to colourPicker show array to hide colour pickers
    colourPickerShow = [false]; // One for font colour, remainder for colours
    for (let i; i < art.numberOfColours; i++) {
      colourPickerShow.push(false);
    }
  }

  function generateArtPreviewVariables(textArray, font, artColourArray, showBackground) {
    let colourArray = artColourArray.map((x) => x.rgb);
    let variables = {
      textArray: JSON.stringify(textArray),
      artId: art.id,
      textColour: font.rgb,
      webp: $webPSupport,
      font: font.fontFamily.name,
      artColourArray: JSON.stringify(colourArray),
      showBackground: showBackground,
    };
    return variables;
  }

  async function generateArtPreview(variables) {
    const IMAGE = gql`
      query GenerateArtPreview(
        $artColourArray: jsonb!
        $artId: Int!
        $font: String!
        $showBackground: Boolean!
        $textArray: jsonb!
        $textColour: String!
        $webp: Boolean!
      ) {
        artPreview(
          artColourArray: $artColourArray
          artId: $artId
          font: $font
          showBackground: $showBackground
          textArray: $textArray
          textColour: $textColour
          webp: $webp
        ) {
          image
        }
      }
    `;
    const res = await client.query({
      query: IMAGE,
      variables: variables,
    });

    const data = await res.data;

    return data.artPreview.image;
  }

  async function updateArtPreviewImage(textArray, font, artColourArray, showBackground) {
    let variables = generateArtPreviewVariables(textArray, font, artColourArray, showBackground);
    let image = await generateArtPreview(variables);
    return image;
  }

  function setPreviewImage(image) {
    art.image = image;
    // If editing exising element we can live update the item on the product
    if ($editingElement) {
      addArt();
    }
  }

  // Make a reactive statement to update preview image src. Need to use .then pattern as img generation is async
  $: imagePromise = updateArtPreviewImage(textArray, font, artColourArray, showBackground, printOption).then(
    setPreviewImage,
  );

  const addArt = async () => {
    let variables = generateArtPreviewVariables(textArray, font, artColourArray, showBackground);
    const image = await generateArtPreview(variables);
    let details = {
      printType: 'art',
      productType: $productDataStore.showing,
      segment: segment,
      image: image,
      textArray: JSON.stringify(textArray),
      artId: art.id,
      art: art,
      textColour: {
        name: font.fill,
        rgb: font.rgb,
      },
      webp: $webPSupport,
      font: font.fontFamily.name,
      artColourArray: JSON.stringify(artColourArray),
      showBackground: showBackground,
      printOption: printOption,
    };
    if ($editingElement) {
      updateInKonva($editingElement, image, details);
    } else {
      addToKonva(image, details);
      // Reset defaults
      artSelectionId.set(null);
      artPanelState.set('categories');

      art = {
        name: 'shield and banner',
        id: 1,
        image: 'https://images.uniformhero.com.au/art/soccer-decal-1.webp',
        backgroundAvailable: true,
        defaultArtColourArray: [
          {
            fill: 'black',
            rgb: '(0,0,0)',
          },
          {
            fill: 'light-blue',
            rgb: '(66, 162, 220)',
          },
        ],
        defaultFont: {
          name: 'apache',
          svg: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 30"><path d="M10.57 17.52V25H5.79V5.84l3.67-3.6h9.94V25h-4.77v-7.48h-4.06zm0-4.33h4.06V6.57h-4.06v6.62zm15.733 6.03V25h-4.77V5.45l3.44-3.21h9.77v16.98h-8.44zm0-4.36h3.67V6.57h-3.67v8.29zm15.248 2.66V25h-4.78V5.84l3.67-3.6h9.94V25h-4.77v-7.48h-4.06zm0-4.33h4.06V6.57h-4.06v6.62zm15.412 7.5h3.84v-2.77h4.79v3.93L62.443 25h-10.26V5.37l3.4-3.13h10.01v8.59h-4.79V6.57h-3.84v14.12zM72.271 2.24v9.35h4.09V2.24h4.77V25h-4.77v-9.1h-4.09V25H67.5V2.24h4.77zM83.164 25V2.24h11.05v4.33h-6.33v4.39h6.01v4.25h-5.95v5.48h6.43V25h-11.21z" transform="translate(-.082 1.38)"/></svg>',
        },
        numberOfColours: 2,
        numberOfTextLines: 2,
        type: 'multicolour',
        defaultTextColourRGB: '(0,0,0)',
        defaultTextColourFill: 'black',
      };

      font = {
        fontFamily: {
          name: 'Aerovias',
          svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 30"><path d="M18.77 20.04h-8.15q-.06.45-.1.91-.03.45-.03.91 0 2.41.94 4.38.94 1.97 2.65 3.65h-3.77q-1.33-1.75-1.96-3.82-.62-2.08-.62-4.25 0-.87.1-1.78H4.37l.73-2.8h3.29q.52-1.85 1.5-3.77t2.34-3.7q1.36-1.78 3.02-3.34 1.66-1.55 3.53-2.72 1.87-1.18 3.86-1.84 2-.66 4.06-.66L20.3 25h-2.86l1.33-4.96zm.76-2.8 3.32-12.43q-1.95.87-3.75 2.14-1.8 1.28-3.32 2.89-1.52 1.6-2.69 3.47t-1.84 3.93h8.28zM30.081 25h-8.03l4.82-18.06h8.04l-.74 2.76h-5.17l-1.29 4.89h3.84l-.73 2.76h-3.88l-1.29 4.85h5.17l-.74 2.8zm2.308 0 4.82-18.06h3.99q1.08 0 2.02.36.95.37 1.65 1.05.69.68 1.1 1.61.4.93.4 2.01 0 1.15-.4 2.23-.41 1.09-1.1 2-.7.9-1.63 1.62-.93.72-1.97 1.14L42.56 25h-2.83l-1.15-5.49h-1.86L35.26 25h-2.87zm5.07-8.25h1.12q.87 0 1.78-.4t1.61-1.08q.69-.68 1.15-1.54.45-.85.45-1.76 0-1.08-.66-1.68-.67-.59-1.71-.59h-1.86l-1.88 7.05zm17.987-9.81q1.26 0 2.17.56.91.56 1.48 1.43.58.87.86 1.97t.28 2.19q0 1.71-.65 3.79-.64 2.08-1.81 3.88-1.17 1.79-2.82 3.02-1.64 1.22-3.59 1.22-1.26 0-2.17-.56-.91-.56-1.48-1.45-.58-.89-.86-1.99t-.28-2.18q0-1.12.3-2.45.29-1.33.84-2.65.54-1.33 1.32-2.56.79-1.22 1.78-2.16 1-.94 2.15-1.5 1.16-.56 2.48-.56zm0 2.76q-1.32 0-2.44 1.01-1.12 1.01-1.94 2.45-.82 1.43-1.28 2.98-.45 1.56-.45 2.68 0 .49.09 1.06.08.58.31 1.1.23.53.61.88.39.34 1.02.34 1.32 0 2.44-.99 1.12-1 1.92-2.43.81-1.43 1.26-3 .46-1.58.46-2.69 0-.46-.09-1.05-.09-.6-.3-1.12-.21-.52-.59-.87-.39-.35-1.02-.35zm18.91-2.76q.1.59.17 1.17.07.57.07 1.17 0 3.07-1.38 5.87-1.38 2.79-3.51 4.92-.94.95-2.1 1.82-1.15.87-2.44 1.57-1.29.7-2.66 1.12-1.36.42-2.69.42l4.82-18.06h2.87l-3.77 14.08q1.6-.81 3.07-2.05 1.47-1.24 2.57-2.77 1.1-1.54 1.76-3.31.67-1.76.67-3.61 0-1.22-.32-2.34h2.87zm6.041 0L75.577 25h-2.87l4.82-18.06h2.87zm5.831 14.99h-5.13q0 .24-.02.47-.02.22-.02.47 0 1.01.28 2.13h-2.83q-.21-1.05-.21-2.13 0-3.08 1.36-5.87 1.37-2.8 3.53-4.93.84-.84 2.05-1.73 1.2-.89 2.56-1.66 1.37-.76 2.75-1.25t2.57-.49L88.288 25h-2.86l.8-3.07zm-4.47-2.76h5.24l2.13-8.04q-2.48 1.22-4.43 3.32-1.96 2.1-2.94 4.72zm8.978 3.03h3.29q.56 0 1.15-.48.59-.49.59-1.12l-1.57-8.63q-.03-.18-.05-.35-.02-.18-.02-.35 0-.87.39-1.66.38-.79 1.01-1.38.63-.59 1.45-.94t1.66-.35h3.81l-.74 2.76q-.28 0-.75-.04-.47-.03-1.03-.03-.52 0-1.06.05-.55.05-.98.23-.44.17-.72.47-.28.3-.28.78v.14l1.57 8.63q.04.18.06.35.01.18.01.35 0 .88-.38 1.66-.39.79-1.01 1.4-.63.61-1.45.96-.83.35-1.66.35h-4.02l.73-2.8z" transform="translate(-3.408 -.55)"/></svg>`,
        },
        fill: 'White',
        rgb: '(255, 255, 255)',
      };

      textArray = [];
      artColourArray = [];
      showBackground = true;
    }
  };

  function artColourSelect(item, idx) {
    artColourArray[idx] = { fill: item.name, rgb: item.rgb };
  }

  function fontColourSelect(item) {
    (font.fill = item.name), (font.rgb = item.rgb);
  }

  onMount(async () => {
    // We will maintain art panel state when switching between tabs
    // So on mount we need to re run appropriate queries.
    // Only do this if we are not editing?
    if (!$editingElement) {
      if (collections.length === 0) {
        collections = await queryArtCollections();
      }
      switch ($artPanelState) {
        case 'subcategories':
          await querySubCategory($artCategoryId);
          break;

        case 'art-item':
          await querySubCategory($artCategoryId);
          // select item
          let item = items.find((x) => x.id === $artSelectionId);
          await selectArt(item);
          break;
      }
    }
  });
</script>

<section>
  {#if $artPanelState === 'categories'}
    <!-- <div class="search-group">
      <Search />
      <input type="text" placeholder="Search here for Artwork" class="input" />
    </div> -->
    <div class="grid-2">
      {#each collections as collection}
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <div class="collection-option" data-idx={collection.id} on:click={() => selectCategory(collection.id)}>
          <ArtCollectionCard
            img={{
              src:
                'https://images.uniformhero.com.au/assets/web/designer-art/' +
                collection.name.replace(' ', '-') +
                '/collection.jpg',
            }}
            label={collection.name}
          />
        </div>
      {/each}
    </div>
    {#if $isMobile}
      <div class="close-button">
        <ColoredButton label="Close" type="red" size="lg" clickFn={() => ($designTabActive = false)} />
      </div>
    {/if}
  {:else if $artPanelState === 'subcategories'}
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <div
      class="back"
      on:click={() => {
        $artPanelState = 'categories';
      }}
    >
      <div class="back-icon">
        {@html icons.arrowLeft}
      </div>
      Back
    </div>
    <div class="grid-3">
      {#each items as item}
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <div
          class=""
          data-idx={item.id}
          on:click={() => {
            selectArt(item);
          }}
        >
          <DesignCard>
            <div class="item">
              <img src={item.image} alt={item.alt} />
              <!-- <span>{item.name}</span> -->
            </div>
          </DesignCard>
        </div>
      {/each}
    </div>
    {#if $isMobile}
      <div class="close-button">
        <ColoredButton label="Close" type="red" size="lg" clickFn={() => ($designTabActive = false)} />
      </div>
    {/if}
  {:else if $artPanelState === 'art-item'}
    {#if !$editingElement}
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      <div
        class="back"
        on:click={() => {
          $artPanelState = 'subcategories';
        }}
      >
        <div class="back-icon">
          {@html icons.arrowLeft}
        </div>
        Back
      </div>
    {/if}

    <div class="preview">
      <img src={art.image} alt="Clip art" style="--bgColour: rgb{selectedProductColourMain.rgb}" />
    </div>
    <div class="grid">
      {#each new Array(art.numberOfTextLines) as _, idx}
        <input
          class="art-input"
          type="text"
          data-idx={idx}
          on:input={updateTextArray}
          placeholder={art.defaultTextArray ? art.defaultTextArray[idx] : 'Enter text'}
          value={$editingElement && textArray[idx] ? textArray[idx] : ''}
        />
      {/each}
    </div>

    <div class="grid-2">
      <div class="">
        <div class="label">Font Family</div>
        <FontOptions bind:options={fonts.family} bind:selected={font.fontFamily} />
      </div>
      <div use:clickOutside on:click_outside={() => handleClickOutside(0)}>
        <div class="label">Text Colour</div>
        {#if art.type === 'multi-colour' || art.type === 'multicolour'}
          <ColourPicker
            selectedRgb={font.rgb}
            clickFn={(item) => fontColourSelect(item)}
            mainColour={selectedProductColourMain}
            trim1Colour={selectedProductColourTrim1}
            trim2Colour={selectedProductColourTrim2}
            bind:show={colourPickerShow[0]}
            selectedProductColourName={$editingElement ? font.fill : null}
          />
        {:else}
          <FontColour options={fonts.colour} bind:selected={font.fill} bind:selectedRgb={font.rgb} />
        {/if}
      </div>
    </div>

    <div class="grid-2">
      {#each new Array(art.numberOfColours) as _, idx}
        {#if art.type === 'multi-colour' || art.type === 'multicolour'}
          <div use:clickOutside on:click_outside={() => handleClickOutside(idx + 1)}>
            <div class="label">Art Colour {idx + 1}</div>
            <ColourPicker
              selectedRgb={art.defaultArtColourArray ? art.defaultArtColourArray[idx].rgb : '(0,0,0)'}
              clickFn={(item) => artColourSelect(item, idx)}
              mainColour={selectedProductColourMain}
              trim1Colour={selectedProductColourTrim1}
              trim2Colour={selectedProductColourTrim2}
              bind:show={colourPickerShow[idx + 1]}
            />
          </div>
        {:else}
          <div>
            <div class="label">Art Colour {idx + 1}</div>
            <PrintColourSelect
              options={fonts.colour}
              selected={art.defaultArtColourArray ? art.defaultArtColourArray[idx].fill : 'white'}
              selectedRgb={art.defaultArtColourArray ? art.defaultArtColourArray[idx].rgb : '(0,0,0)'}
              clickFn={(item) => artColourSelect(item, idx)}
            />
          </div>
        {/if}
      {/each}

      {#if art.backgroundAvailable && art.backgroundCanBeRemoved}
        <div>
          <div class="label">Background</div>
          <Switch bind:value={showBackground} label="" design="inner" labelTrue="Yes" labelFalse="No" />
        </div>
      {/if}

      {#if printOptions.length > 1}
        <div class="print-type-selection">
          <PrintTypeSelection {printOptions} bind:printOption />
        </div>
      {/if}
    </div>
    <div class="grid-2">
      {#if !$editingElement}
        <ColoredButton label="+ Add Art" type="green" clickFn={addArt} />
      {/if}
      {#if $isMobile && $designTabActive}
        {#if $editingElement}
          <ColoredButton label="Update" type="green" clickFn={() => ($designTabActive = false)} />
        {/if}
        <ColoredButton label="Close" type="red" clickFn={() => ($designTabActive = false)} />
      {/if}
    </div>
  {/if}
</section>

<style>
  section {
    padding: 2.2rem 0;
  }

  .close {
    margin-bottom: 1rem;
    width: fit-content;
    font-size: 2rem;
    font-weight: bold;
    color: #fe5900;
    margin-left: auto;
    margin-right: 0;
    box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.1);
    border-radius: 10rem;
    padding: 1rem 1.5rem;
    text-transform: uppercase;
  }

  .close-button {
    margin-top: 3rem;
    width: 50%;
    margin-left: auto;
    margin-right: auto;
  }

  .search-group {
    border: 1px solid #6d5bd0;
    border-radius: 1rem;
    padding: 0.8rem;
    display: flex;
    align-items: center;
    color: #6d5bd0;
    background-color: #f4f2ff;
  }

  .input {
    border: none;
    width: 100%;
    margin-left: 1rem;
    font-size: 1.6rem;
    background-color: transparent;
  }

  .label {
    font-weight: bold;
    color: #666;
    letter-spacing: 1.5px;
    font-size: 1.2rem;
    text-transform: uppercase;
    margin-bottom: 0.5rem;
    display: block;
  }

  .collection-option {
    max-height: 150px;
  }

  .preview {
    height: 150px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .preview img {
    background-color: var(--bgColour);
    padding: 1rem;
    max-height: 90%;
  }

  .back {
    margin-top: 1rem;
    padding: 0.5rem;
    height: 3rem;
    font-size: 1.3rem;
    border-radius: 0.5rem;
    display: inline-flex;
    align-items: center;
    background-color: #fff;
    box-shadow: 1px 1px 0.5rem rgba(0, 0, 0, 0.15);
    color: #777;
    cursor: pointer;
  }

  .back-icon {
    margin-right: 0.5rem;
  }

  .grid input {
    padding: 1.5rem;
    border: 2px dashed #d6d3d3;
    border-radius: 1rem;
  }

  .input:focus {
    outline: none;
  }

  .item-center {
    align-items: center;
  }

  .grid-3 {
    margin-top: 2rem;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 2rem;
  }

  .grid-2 {
    margin-top: 2rem;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 2rem;
  }

  .grid {
    margin-top: 1rem;
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }

  .item {
    display: flex;
    align-items: center;
    width: 100%;
  }

  .item img {
    max-width: 100%;
  }

  .print-type-selection {
    grid-column: 1/3;
  }
</style>
